// BUILD-046 consolidated pass 2026-10-23, item 4: what the "Edit preset" panel edits - a DRAFT of one preset - and how the draft becomes the preset's saved settings.
//
// The panel starts from the preset's saved settings (`draftFromPreset`), changes only the draft while the person edits (the table behind it is not touched), and `criteriaFromDraft` turns the draft
// back into criteria on Save. Two guards keep a preset from changing by accident:
//   - a field the person did not touch is saved exactly as it was (the sort is rebuilt only when `sortTouched`; the Customer / Job, Customer and Assigned to filters start as saved and change only when the person
//     chooses another with the page's own searchable pickers, or clears them);
//   - the sort a preset saved as ONE mode keeps that shape unless the person edits the sort, so editing the name alone never turns a mode into rules.
// The sort has up to three rows (Sort by, Then by, Then by) over the same keys and directions the rules have always used; a row is None, a key and its direction. A single mode that no rule can
// stand for (Oldest, Newest, Project, Needs attention) is shown as the first row "As saved: <label>" and is kept until the person chooses something else.
//
// Pure module. Runtime imports: the page sort modes and the presets model. Erasable TypeScript only.
import { MODE_ORDER, PAGE_SORT_LABELS, isColumnSortMode } from "./pageSortModel";
import type { PageSortMode } from "./pageSortModel";
import { MAX_SORT_RULES, normalizeCriteria, ruleKeysFor } from "./presetsModel";
import type { Preset, PresetCriteria, PresetTab, RuleKey, SortRule } from "./presetsModel";
import type { OrderDirection } from "./taskOrdering";
import type { JobFilter } from "./jobFilterModel";
import type { CustomerFilter } from "./customerFilterModel";
import type { AssigneeFilter } from "./assigneeFilterModel";

/** The first direction of each key is the one chosen when the key is chosen (the order a click on its heading gives first). */
export const RULE_DIRECTIONS: Readonly<Record<RuleKey, readonly OrderDirection[]>> = Object.freeze({
  title: ["asc", "desc"],
  assignee: ["asc", "desc"],
  priority: ["desc", "asc"],
  due: ["asc", "desc"],
  status: ["asc", "desc"],
  responsible: ["asc", "desc"],
  progress: ["asc", "desc"],
  assigned: ["desc", "asc"],
});

export const SAVED_MODE = "saved-mode" as const;
export type SortRowKey = RuleKey | "" | typeof SAVED_MODE;

export interface SortRow {
  key: SortRowKey;
  direction: OrderDirection;
}

const NONE_ROW: SortRow = Object.freeze({ key: "", direction: "asc" }) as SortRow;

export interface EditDraft {
  name: string;
  status: string;
  quick: string;
  open: string;
  department: string;
  /** The Customer / Job and Assigned to filters of the draft: the saved ones to begin with, then whatever the person chooses with the searchable pickers or clears (any job / anyone). */
  job: JobFilter;
  /** The Customer filter of the draft: the saved one to begin with, then whatever the person chooses with the searchable picker or clears (any customer). */
  customer: CustomerFilter;
  assignee: AssigneeFilter;
  /** Always MAX_SORT_RULES rows. */
  sort: SortRow[];
  /** False until the person changes a sort row: then the sort is saved exactly as it was. */
  sortTouched: boolean;
}

/** The rule a single sort mode stands for on this tab, or null when no rule can (Recommended, Oldest, Newest, Project, Needs attention, or a key the tab does not have). */
export function modeAsRule(tab: PresetTab, mode: PageSortMode): SortRule | null {
  let rule: SortRule | null = null;
  if (isColumnSortMode(mode)) rule = { key: MODE_ORDER[mode].key, direction: MODE_ORDER[mode].direction };
  else if (mode === "responsible-asc") rule = { key: "responsible", direction: "asc" };
  else if (mode === "responsible-desc") rule = { key: "responsible", direction: "desc" };
  else if (mode === "progress-asc") rule = { key: "progress", direction: "asc" };
  else if (mode === "progress-desc") rule = { key: "progress", direction: "desc" };
  else if (mode === "assigned-newest") rule = { key: "assigned", direction: "desc" };
  else if (mode === "assigned-oldest") rule = { key: "assigned", direction: "asc" };
  return rule !== null && ruleKeysFor(tab).includes(rule.key) ? rule : null;
}

/** The sort rows a preset's criteria show: its rules, or the one mode as a first row, padded with None to three rows. */
export function sortRowsOf(tab: PresetTab, criteria: PresetCriteria): SortRow[] {
  const rows: SortRow[] = [];
  if (criteria.rules.length > 0) {
    for (const rule of criteria.rules) rows.push({ key: rule.key, direction: rule.direction });
  } else if (criteria.mode !== "recommended") {
    const rule = modeAsRule(tab, criteria.mode);
    rows.push(rule === null ? { key: SAVED_MODE, direction: "asc" } : { key: rule.key, direction: rule.direction });
  }
  while (rows.length < MAX_SORT_RULES) rows.push({ ...NONE_ROW });
  return rows.slice(0, MAX_SORT_RULES);
}

/** The label of a saved single mode that no rule can stand for ("Oldest"), for the first row's "As saved: ..." choice. */
export function savedModeLabel(mode: PageSortMode): string {
  return PAGE_SORT_LABELS[mode];
}

/** The statuses a My Tasks preset can hold, with the words the status tabs use. */
export const STATUS_CHOICES: readonly { value: string; label: string }[] = Object.freeze([
  { value: "open", label: "All open" },
  { value: "active", label: "Active" },
  { value: "in_progress", label: "In progress" },
  { value: "waiting", label: "Waiting" },
  { value: "blocked", label: "Blocked" },
  { value: "completed", label: "Completed" },
]);

/**
 * The draft of a preset: its saved settings. A blank saved choice is the page default it always meant (My Tasks: Active and All; Company / Team: Open), so the select shows the choice that applies.
 */
export function draftFromPreset(tab: PresetTab, preset: Pick<Preset, "name" | "criteria">): EditDraft {
  const c = normalizeCriteria(tab, preset.criteria);
  return {
    name: preset.name,
    status: tab === "my-work" ? (c.status === "" ? "active" : c.status) : tab === "issued" || tab === "group-shared" ? (c.status === "" ? "open" : c.status) : "",
    quick: tab === "my-work" ? (c.quick === "" ? "all" : c.quick) : "",
    open: tab === "team-work" ? (c.open === "" ? "open" : c.open) : "",
    department: c.department,
    job: c.job,
    customer: c.customer,
    assignee: c.assignee,
    sort: sortRowsOf(tab, c),
    sortTouched: false,
  };
}

/** Changes one sort row and keeps the rows consistent: choosing None empties it and every row after it; choosing "As saved" is first-row only and empties the rest; a new key starts at its first direction. */
export function setSortRow(rows: readonly SortRow[], index: number, patch: Partial<SortRow>): SortRow[] {
  const next: SortRow[] = rows.map((row) => ({ ...row }));
  if (index < 0 || index >= next.length) return next;
  if (patch.key !== undefined) {
    if (patch.key === "") {
      for (let at = index; at < next.length; at += 1) next[at] = { ...NONE_ROW };
    } else if (patch.key === SAVED_MODE) {
      next[index] = { key: SAVED_MODE, direction: "asc" };
      for (let at = index + 1; at < next.length; at += 1) next[at] = { ...NONE_ROW };
    } else {
      next[index] = { key: patch.key, direction: RULE_DIRECTIONS[patch.key][0] };
    }
  }
  if (patch.direction !== undefined && next[index].key !== "" && next[index].key !== SAVED_MODE) next[index] = { ...next[index], direction: patch.direction };
  return next;
}

/** The criteria a draft saves, on top of the preset's own (`base`): untouched fields keep their saved value, and the result is normalised for the tab like any other. */
export function criteriaFromDraft(tab: PresetTab, base: PresetCriteria, draft: EditDraft): PresetCriteria {
  let mode: PageSortMode = base.mode;
  let rules: SortRule[] = base.rules;
  if (draft.sortTouched) {
    rules = [];
    mode = "recommended";
    if (draft.sort[0]?.key === SAVED_MODE) {
      mode = base.rules.length === 0 ? base.mode : "recommended";
    } else {
      for (const row of draft.sort) {
        if (row.key === "" || row.key === SAVED_MODE) break;
        rules.push({ key: row.key, direction: row.direction });
      }
    }
  }
  return normalizeCriteria(tab, {
    ...base,
    status: draft.status,
    quick: draft.quick,
    open: draft.open,
    department: draft.department,
    job: draft.job,
    customer: draft.customer,
    assignee: draft.assignee,
    mode,
    rules,
  });
}
